import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** Header/drawer compact count-up plays once per browser session. */
const COMPACT_PLAYED_KEY = 'pb-raised-compact-played';

/** Exact NZD cents captured on an NZ-market page (avoids FX ±$1). */
const NZD_CENTS_KEY = 'pb_raised_nzd_cents';

/** Forced shop-currency format — never use presentment £ from Markets. */
const NZD_FORMAT = '${{amount_no_decimals}}';
const SHOP_CURRENCY = 'NZD';

/**
 * @typedef {Object} PbTotalRaisedRefs
 * @property {HTMLElement} [amountValue]
 * @property {HTMLElement} [bar]
 * @property {HTMLElement} [fill]
 */

/**
 * Count-up for raised totals (header compact + sponsor-band full panel).
 * Always presents the exact NZD fundraising total (no Markets FX drift).
 * @extends {Component<PbTotalRaisedRefs>}
 */
class PbTotalRaised extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #raf = null;

  /** @type {boolean} */
  #played = false;

  get #isCompact() {
    return this.classList.contains('pb-total--compact');
  }

  static #compactAlreadyPlayed() {
    try {
      return sessionStorage.getItem(COMPACT_PLAYED_KEY) === '1';
    } catch {
      return false;
    }
  }

  static #markCompactPlayed() {
    try {
      sessionStorage.setItem(COMPACT_PLAYED_KEY, '1');
    } catch {
      // Private mode / blocked storage — ignore
    }
  }

  static #readCachedNzdCents() {
    try {
      const raw = sessionStorage.getItem(NZD_CENTS_KEY);
      if (raw == null) return null;
      const cents = Number.parseInt(raw, 10);
      return Number.isFinite(cents) ? cents : null;
    } catch {
      return null;
    }
  }

  static #writeCachedNzdCents(cents) {
    try {
      sessionStorage.setItem(NZD_CENTS_KEY, String(cents));
    } catch {
      // Private mode / blocked storage — ignore
    }
  }

  /**
   * @param {number} presentmentCents
   * @param {string} amountCurrency
   * @param {boolean} isShopCurrencyPage - true only when Liquid rendered NZ market cents
   */
  static toShopCents(presentmentCents, amountCurrency, isShopCurrencyPage) {
    if (isShopCurrencyPage) {
      PbTotalRaised.#writeCachedNzdCents(presentmentCents);
      return presentmentCents;
    }

    const cached = PbTotalRaised.#readCachedNzdCents();
    if (typeof cached === 'number') return cached;

    const currency = (amountCurrency || SHOP_CURRENCY).toUpperCase();
    const active = (window.Shopify?.currency?.active || currency).toUpperCase();
    const rate = Number.parseFloat(window.Shopify?.currency?.rate);
    if (active !== SHOP_CURRENCY && Number.isFinite(rate) && rate > 0) {
      return Math.round(presentmentCents / rate);
    }

    return presentmentCents;
  }

  connectedCallback() {
    super.connectedCallback();

    queueMicrotask(() => this.#paintNzd());

    if (this.#played) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this.#played = true;
      return;
    }

    if (this.#isCompact && PbTotalRaised.#compactAlreadyPlayed()) {
      this.#played = true;
      return;
    }

    const options = this.#isCompact
      ? { threshold: 0.5 }
      : { threshold: 0.35, rootMargin: '0px 0px -8% 0px' };

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#animate();
        break;
      }
    }, options);
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#raf != null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
    super.disconnectedCallback();
  }

  #shopCents() {
    const presentmentCents = Number.parseInt(this.dataset.raisedCents || '0', 10) || 0;
    const amountCurrency =
      this.dataset.amountCurrency || window.Shopify?.currency?.active || SHOP_CURRENCY;
    const isShopCurrencyPage = this.dataset.shopCurrencyPage === 'true';
    return PbTotalRaised.toShopCents(presentmentCents, amountCurrency, isShopCurrencyPage);
  }

  #targetProgress() {
    const goal = Number.parseInt(this.dataset.goalCents || '0', 10) || 0;
    if (goal > 0) {
      return Math.min(100, Math.max(0, (this.#shopCents() / goal) * 100));
    }
    const value = Number.parseFloat(this.dataset.progress || '0');
    if (Number.isNaN(value)) return 0;
    return Math.min(100, Math.max(0, value));
  }

  #format(cents) {
    return formatMoney(Math.round(cents), NZD_FORMAT, SHOP_CURRENCY);
  }

  #paintAmount(cents) {
    const { amountValue } = this.refs;
    if (amountValue) amountValue.textContent = this.#format(cents);
  }

  #paintProgress(progress) {
    if (this.#isCompact) return;
    this.style.setProperty('--pb-progress', `${progress}%`);
    const { bar } = this.refs;
    if (bar) bar.setAttribute('aria-valuenow', String(Math.round(progress)));
  }

  #paintNzd() {
    this.#paintAmount(this.#shopCents());
    this.#paintProgress(this.#targetProgress());
    this.dataset.ready = 'true';
  }

  #resetVisual() {
    this.#paintProgress(0);
    this.#paintAmount(0);
  }

  #showFinal() {
    this.#paintNzd();
    this.#played = true;
  }

  #animate() {
    if (this.#played) return;
    if (this.#isCompact && PbTotalRaised.#compactAlreadyPlayed()) return;

    this.#played = true;
    if (this.#isCompact) PbTotalRaised.#markCompactPlayed();
    this.#resetVisual();

    const targetCents = this.#shopCents();
    const targetProgress = this.#isCompact ? 0 : this.#targetProgress();
    const duration = 1200;
    const start = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const e = easeOut(t);
      const cents = Math.round((targetCents * e) / 100) * 100;
      const progress = targetProgress * e;

      this.#paintProgress(progress);
      this.#paintAmount(cents);

      if (t < 1) {
        this.#raf = requestAnimationFrame(tick);
      } else {
        this.#raf = null;
        this.#showFinal();
      }
    };

    this.#raf = requestAnimationFrame(tick);
  }
}

if (!customElements.get('pb-total-raised')) {
  customElements.define('pb-total-raised', PbTotalRaised);
}
